import React, { useState, Suspense, lazy } from 'react';
import { STATUS_COLOR } from '../constants/statusColors';
import { useTranslation } from 'react-i18next';
import { localizeGameTitle } from '../utils/localize';
import { isRightClickMenuEnabled } from '../utils/gameCardPrefs';
import { Card, CardContent, CardMedia, Typography, IconButton, Tooltip, Box, SvgIcon, SvgIconProps, Button, CircularProgress, useMediaQuery, useTheme, PopoverProps } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Game, Platform } from '../types/Game';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import BeenhereIcon from '@mui/icons-material/Beenhere';
import { setGameStatus, setGamePriority } from '../services/api';
import { logSteamLaunch } from '../services/logApi';
import ConfirmDialog from './dialogs/ConfirmDialog';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button as MuiButton } from '@mui/material';
import NotStartedOutlinedIcon from '@mui/icons-material/NotStartedOutlined';
import GameCardStatusMenu from './game/GameCardStatusMenu';
import { getPlatformDisplayText } from '../utils/PlatformList';
import { effectivePlayTimeHours } from '../utils/playtime';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import GameReviewDialog, { EMPTY_REVIEW_DATA } from './GameReviewDialog';
import { GameReviewData } from '../types/Review';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import { saveGameMemo, deleteGameMemo, getGameMemo } from '../services/api';
import BlockIcon from '@mui/icons-material/Block';
import PrioritySlider from './common/PrioritySlider';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';

import GameReviewPreviewDialog from './dialogs/GameReviewPreviewDialog';
import FolderPickerPopover from './folder/FolderPickerPopover';
import GameListPickerPopover from './GameList/GameListPickerPopover';
import { Folder } from '../services/folders_api';
import { cardHoverSx, CARD_EDGE_CLASS, CARD_EDGE_SX, CARD_ZOOM_CLASS, CARD_SHEEN_CLASS, CARD_SHEEN_SX } from '../constants/cardHover';
import { useCardScoreIconColor } from './profile/accentScope';

// Lexical ベースのエディタ／プレビュー：別チャンクで遅延ロード
const MemoEditorDialog = lazy(() => import('./editor/MemoEditorDialog'));
const MemoPreviewDialog = lazy(() => import('./editor/MemoPreviewDialog'));

export type GameCardSize = 'standard' | 'square' | 'landscape';

// GameReviewDialog に渡す「空のプラットフォーム配列」。毎レンダリングで [] を作ると
// 参照が変わり、ダイアログの初期化 effect が再実行されて編集中の内容がリセットされる。
// 中身は常に空なのでモジュール定数で参照を固定する。
const EMPTY_PLATFORMS: Platform[] = [];

interface GameCardProps {
  game: Game;
  mode?: 'profile' | 'public';
  username?: string;
  initialStatus?: number | null;
  initialScore?: number | null;
  initialReviewText?: string | null;
  initialPlayStartDate?: string | null;
  initialPlayEndDate?: string | null;
  initialPlayTime?: number | null;
  initialSteamPlaytimeMin?: number | null;
  initialSubStatus?: number[] | null;
  initialScoreTags?: string[] | null;
  initialPlatform?: number[] | null;
  initialPriority?: number;
  initialHasMemo?: boolean;
  initialReviewId?: number | null;
  priorityMode?: boolean;
  userSteamId?: string | null;
  cardSize?: GameCardSize;
  uiVisible?: boolean;
  titleVisible?: boolean;
  cardRadius?: string;
  // フォルダ機能（ライブラリ内のみ）
  folders?: Folder[];
  onFolderChange?: () => void;
}

const GradientBeenhereIcon: React.FC<SvgIconProps> = (props) => (
  <SvgIcon {...props}>
    <defs>
      <linearGradient id="beenhereGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#2088ff" />
        <stop offset="100%" stopColor="#a455ff" />
      </linearGradient>
    </defs>
    <BeenhereIcon sx={{ fill: 'url(#beenhereGradient)' }} />
  </SvgIcon>
);

const GameCard: React.FC<GameCardProps> = ({
  game,
  mode,
  username,
  initialStatus,
  initialScore,
  initialReviewText,
  initialPlayStartDate,
  initialPlayEndDate,
  initialPlayTime,
  initialSteamPlaytimeMin,
  initialSubStatus,
  initialScoreTags,
  initialPlatform,
  initialPriority = 0,
  initialHasMemo = false,
  initialReviewId,
  priorityMode = false,
  userSteamId,
  cardSize = 'standard',
  uiVisible = true,
  titleVisible = true,
  cardRadius = '0px',
  folders,
  onFolderChange,
}) => {
  const { t, i18n } = useTranslation('gameCard');
  const lang = i18n.language;
  const gameTitle = localizeGameTitle(game, lang);
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const scoreIconColor = useCardScoreIconColor();
  // 左クリックは要素、右クリックはカーソル位置の仮想アンカー要素を入れるため MUI のアンカー型にする
  const [anchorEl, setAnchorEl] = useState<PopoverProps['anchorEl']>(null);
  const [status, setStatus] = useState<number | null>(initialStatus ?? null);
  const [isInitialStatus, setIsInitialStatus] = useState<boolean>(true);
  const [isStatusChanging, setIsStatusChanging] = useState(false);
  const [score, setScore] = useState<number | null>(initialScore ?? null);
  const [reviewText, setReviewText] = useState<string | null>(initialReviewText ?? null);
  const [playStartDate, setPlayStartDate] = useState<string | null>(initialPlayStartDate ?? null);
  const [playEndDate, setPlayEndDate] = useState<string | null>(initialPlayEndDate ?? null);
  const [playTime, setPlayTime] = useState<number | null>(initialPlayTime ?? null);
  const navigate = useNavigate();
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
  const [subStatus, setSubStatus] = useState<number[] | null>(initialSubStatus ?? null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  // 編集モード（true: ダイアログが自己 fetch / false: 新規モードで empty 初期値）
  const [reviewDialogIsEdit, setReviewDialogIsEdit] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState<boolean>(initialStatus === 5);
  const [priority, setPriority] = useState<number>(initialPriority);
  const [hasMemo, setHasMemo] = useState<boolean>(initialHasMemo);
  const [memoPreviewOpen, setMemoPreviewOpen] = useState(false);
  const [memoEditorOpen, setMemoEditorOpen] = useState(false);
  const [memoEditorInitial, setMemoEditorInitial] = useState<string>('');
  const [reviewPreviewOpen, setReviewPreviewOpen] = useState(false);
  const [folderPickerAnchor, setFolderPickerAnchor] = useState<PopoverProps['anchorEl']>(null);
  const [listPickerAnchor, setListPickerAnchor] = useState<PopoverProps['anchorEl']>(null);

  React.useEffect(() => {
    setPriority(initialPriority);
  }, [initialPriority, priorityMode]);

  React.useEffect(() => {
    const checkLoginStatus = () => {
      const token = localStorage.getItem('token');
      setIsLoggedIn(!!token);
    };
    checkLoginStatus();
  }, []);

  React.useEffect(() => {
    setStatus(initialStatus ?? null);
    setIsInitialStatus(true);
    setScore(initialScore ?? null);
    setReviewText(initialReviewText ?? null);
    setPlayStartDate(initialPlayStartDate ?? null);
    setPlayEndDate(initialPlayEndDate ?? null);
    setPlayTime(initialPlayTime ?? null);
    setSubStatus(initialSubStatus ?? null);
  }, [
    initialStatus,
    initialScore,
    initialReviewText,
    initialPlayStartDate,
    initialPlayEndDate,
    initialPlayTime,
    initialSubStatus
  ]);

  React.useEffect(() => {
    setIsWishlisted(initialStatus === 5);
  }, [initialStatus]);

  React.useEffect(() => {
    setHasMemo(initialHasMemo);
  }, [initialHasMemo]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (mode === 'public') return; // パブリックモードでは操作を無効化
    setAnchorEl(event.currentTarget);
    setIsStatusChanging(true);
  };

  // PC: カードを右クリックしてもステータスメニューを開けるようにする。
  // 公開プロフィール・未発売（ウィッシュボタン表示）・モバイル（長押し）では
  // 通常の右クリック/長押しを尊重し、何もしない（＝ブラウザ標準メニューのまま）。
  const handleContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    // プロフィール/一覧の全カードで有効（未発売も区別しない）。
    // 公開プロフィール・モバイル（長押し）・設定OFF のときだけ無効。
    if (mode === 'public' || !isMdUp || !isRightClickMenuEnabled()) return;
    event.preventDefault();
    event.stopPropagation();
    // コンテキストメニューの慣習どおり、カーソル位置に開く（仮想アンカー要素）。
    // 同じ anchorEl を使うフォルダピッカー等もこの位置に揃う。
    const x = event.clientX;
    const y = event.clientY;
    setAnchorEl({
      nodeType: 1,
      getBoundingClientRect: () => new DOMRect(x, y, 0, 0),
    } as PopoverProps['anchorEl']);
    setIsStatusChanging(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setIsStatusChanging(false);
  };

  const handleStatusChange = async (newStatus: number | null) => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    // 削除時は確認ダイアログを表示
    if (newStatus === null) {
      setOpenDeleteConfirmDialog(true);
      return;
    }

    try {
      await setGameStatus(game.game_id, newStatus);
      setStatus(newStatus);
      setIsInitialStatus(false);
      
      // 初回のステータス設定時のみダイアログを表示（status === nullの場合）
      if (mode !== 'profile' && mode !== 'public' && 
          newStatus !== null && 
          [1, 2, 3].includes(newStatus) && 
          status === null) {
        setOpenReviewDialog(true);
      }
    } catch (error: any) {
      setErrorMessage(error.message || t('errorOccurred'));
      setOpenErrorDialog(true);
    }
    handleClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      await setGameStatus(game.game_id, null);
      setStatus(null);
    } catch (error: any) {
      setErrorMessage(error.message || t('errorOccurred'));
      setOpenErrorDialog(true);
    }
    setOpenDeleteConfirmDialog(false);
    handleClose();
  };

  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isStatusChanging) {
      navigate(`/game/${game.game_id}`);
    }
  };

  const getStatusIcon = (statusCode: number | null) => {
    // サブステータスに基づいて色を変更
    const isUnfinished = subStatus?.includes(8);
    const isRetired = subStatus?.includes(9);

    switch (statusCode) {
      case 1: 
        if (isRetired) {
          return <BlockIcon fontSize="small" sx={{ color: 'error.main', opacity: 0.85 }} />;
        } else if (isUnfinished) {
          return <RemoveCircleOutlineIcon fontSize="small" sx={{ color: STATUS_COLOR[1], opacity: 0.6 }} />;
        }
        return <CheckCircleOutlineIcon fontSize="small" sx={{ color: STATUS_COLOR[1] }} />;
      case 2:
        if (isRetired) {
          return <PlayCircleOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />;
        } else if (isUnfinished) {
          return <PlayCircleOutlineIcon fontSize="small" sx={{ color: 'info.main', opacity: 0.5 }} />;
        }
        return <PlayCircleOutlineIcon fontSize="small" sx={{ color: 'info.main' }} />;
      case 3:
        if (isRetired) {
          return <BlockIcon fontSize="small" sx={{ color: 'error.main', opacity: 0.85 }} />;
        } else if (isUnfinished) {
          return <RemoveCircleOutlineIcon fontSize="small" sx={{ color: 'warning.main', opacity: 0.5 }} />;
        }
        return <PauseCircleOutlineIcon fontSize="small" sx={{ color: 'warning.main' }} />;
      case 4: return <NotStartedOutlinedIcon fontSize="small" sx={{ color: '#b99a71' }} />;
      case 5: return <StarBorderIcon fontSize="small" sx={{ color: 'secondary.main' }} />;
      default: return <AddCircleOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 1: return t('status.1');
      case 2: return t('status.2');
      case 3: return t('status.3');
      case 4: return t('status.4');
      case 5: return t('status.5');
      default: return t('status.none');
    }
  };

  const getFormattedReleaseYear = (releaseDate: string | null | undefined) => {
    if (!releaseDate) return t('releaseYear.unknown');
    
    const release = new Date(releaseDate);
    const now = new Date();
    const threeYearsFromNow = new Date();
    threeYearsFromNow.setFullYear(now.getFullYear() + 3);

    // 3年以上先の発売日の場合は「未定」を返す
    if (release > threeYearsFromNow) {
      return t('releaseYear.tbd');
    }

    return release.getFullYear();
  };

  const renderPlayInfo = () => {
    if (mode !== 'profile' && mode !== 'public') return null;

    // 表示用の実効プレイ時間（手動優先・無ければSteam累計）。編集には playTime(生)を使い続ける。
    const displayPlayTime = effectivePlayTimeHours(playTime, initialSteamPlaytimeMin ?? null);

    // プレイ時間だけがある場合の表示
    if (!playStartDate && displayPlayTime) {
      return (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          minWidth: 0 
        }}>
          <TimerOutlinedIcon sx={{ mr: 0.25, color: 'text.secondary', flexShrink: 0, fontSize: { xs: '0.85rem', sm: '1rem' } }} />
          <Typography variant="caption" sx={{ lineHeight: 1, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayPlayTime}h
          </Typography>
        </Box>
      );
    }

    if (playStartDate) {
      const startDate = new Date(playStartDate);
      const formattedDate = `${startDate.getFullYear()}/${String(startDate.getMonth() + 1).padStart(2, '0')}`;

      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          gap: { xs: '0.1em', sm: '0.5em' },
          minWidth: 0,
          flex: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <PlayCircleOutlineIcon sx={{ mr: 0.25, color: 'text.secondary', flexShrink: 0, fontSize: { xs: '0.85rem', sm: '1rem' } }} />
            <Typography variant="caption" sx={{ lineHeight: 1, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {formattedDate}
            </Typography>
          </Box>
          {displayPlayTime ? (
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <TimerOutlinedIcon sx={{ mr: 0.25, color: 'text.secondary', flexShrink: 0, fontSize: { xs: '0.85rem', sm: '1rem' } }} />
              <Typography variant="caption" sx={{ lineHeight: 1, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayPlayTime}h
              </Typography>
            </Box>
          ) : null}
        </Box>
      );
    }

    // プレイ期間や時間がない場合は発売年を表示
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {getFormattedReleaseYear(game.first_release_date)}
      </Typography>
    );
  };

  const renderReplayIcon = () => {
    if (mode !== 'profile' && mode !== 'public' || !subStatus || !subStatus.includes(1)) return null;

    return (
      <Box
        sx={{
          background: 'linear-gradient(45deg, #f0f0f0d8, #bebebed2)',
          borderRadius: '6px',
          padding: '2px 4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 0.5,
        }}
      >
        <Tooltip title={t('badge.replay')} arrow>
          <RefreshIcon sx={{ color: '#383838', fontSize: { xs: '0.9rem', sm: '1rem' } }} />
        </Tooltip>
      </Box>
    );
  };

  const renderEndlessIcon = () => {
    if (mode !== 'profile' && mode !== 'public' || !subStatus || !subStatus.includes(0)) return null;

    return (
      <Box
        sx={{
          background: 'linear-gradient(45deg, #37474fc4, #455a64a4)',
          borderRadius: '6px',
          padding: '2px 4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 0.5,
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }}
      >
        <Tooltip title={t('badge.endless')} arrow>
          <AllInclusiveIcon sx={{ color: '#e0e0e0', fontSize: { xs: '0.9rem', sm: '1rem' } }} />
        </Tooltip>
      </Box>
    );
  };

  const renderAchievementIcon = () => {
    if (mode !== 'profile' && mode !== 'public' || !subStatus) return null;

    // 実績コンプリート（プレイ済み/プレイ中でサブステータス7）の場合
    if ((status === 1 || status === 2) && subStatus.includes(7)) {
      return (
        <Box
          sx={{
            background: 'linear-gradient(45deg, #2088ffc4, #a455ffa4)',
            borderRadius: '6px',
            padding: '2px 4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 0.5,
          }}
        >
          <Tooltip title={t('badge.achievementComplete')} arrow>
            <EmojiEventsIcon sx={{ color: 'white', fontSize: { xs: '0.9rem', sm: '1rem' } }} />
          </Tooltip>
        </Box>
      );
    }

    return null;
  };

  const hasUpwardBias = (scoreTags: string[] | null | undefined) => {
    if (!scoreTags) return false;
    const tags = scoreTags.map(Number);
    // 9が下方バイアス、それ以外は上方バイアス
    return tags.some(tag => tag !== 9);
  };

  const renderScore = () => {
    if (score === null || score === undefined || mode !== 'profile' && mode !== 'public') return null;

    const isUpwardBiased = hasUpwardBias(initialScoreTags);

    return (
      <Box
        sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          borderRadius: '6px',
          padding: '2px 6px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {isUpwardBiased ? (
          <KeyboardDoubleArrowUpIcon sx={{ color: scoreIconColor, fontSize: '0.9rem', mr: 0.5 }} />
        ) : (
          <StarIcon sx={{ color: scoreIconColor, fontSize: '0.9rem', mr: 0.5 }} />
        )}
        <Typography 
          variant="caption" 
          sx={{ 
            color: 'white', 
            fontWeight: 'bold', 
            lineHeight: 1.2,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {typeof score === 'number' ? score.toFixed(1) : score}
        </Typography>
      </Box>
    );
  };

  // JPスコアの色を決定する関数（GameRatingと同じロジック）
  const getJpScoreColor = (score: number | null) => {
    if (!score) return '#666666';
    if (score >= 90) return '#4CAF50';
    if (score >= 80) return '#8BC34A';
    if (score >= 70) return '#CDDC39';
    if (score >= 60) return '#FFEB3B';
    if (score >= 50) return '#FFC107';
    if (score >= 41) return '#FF9800';
    return '#F44336';
  };

  // JPスコアを表示する関数
  const renderJpScore = () => {
    const jpRating = game.rating_jp;
    const jpRatingCount = game.rating_jp_count;
    if (!jpRating || jpRating === null || jpRating === undefined || mode === 'profile' || mode === 'public' || isUpcomingGame) return null;

    // jpRatingが数値型であることを確認
    const numericRating = Number(jpRating);
    if (isNaN(numericRating)) return null;

    const scoreColor = getJpScoreColor(numericRating);
    const normalizedScore = (numericRating / 20) * 10 / 10; // 100点満点を5点満点に変換
    const jpScore = numericRating.toFixed(1); // 小数点を1桁に変更
    const progressValue = numericRating; // 0-100の値をそのまま使用
    const isLowRatingCount = jpRatingCount !== null && jpRatingCount !== undefined && jpRatingCount < 3;

    return (
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: 2, sm: 4 },
          right: { xs: 2, sm: 4 },
          width: { xs: '40px', md: '48px' },
          height: { xs: '40px', md: '48px' },
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          borderRadius: '50%',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mt: score !== null ? 0.5 : (priority > 0 && (status !== 1 && status !== 2) ? 0.5 : 0),
        }}
      >
        {/* レーティング数が少ない場合の警告アイコン */}
        {isLowRatingCount && (
          <Tooltip title={t('badge.lowRatingCount')} arrow>
            <InfoOutlinedIcon
              sx={{
                position: 'absolute',
                top: -4,
                right: -4,
                fontSize: { xs: '20px', sm: '22px' },
                zIndex: 3,
                color: '#e0d31a',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                borderRadius: '50%',
                padding: '1px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            />
          </Tooltip>
        )}

        {/* 円形プログレスバー背景 */}
        <CircularProgress
          variant="determinate"
          value={100}
          size={isMdUp ? 40 : 32}
          thickness={2}
          sx={{
            position: 'absolute',
            color: 'rgba(255, 255, 255, 0.1)',
          }}
        />
        
        {/* 円形プログレスバー */}
        <CircularProgress
          variant="determinate"
          value={progressValue}
          size={isMdUp ? 40 : 32}
          thickness={2}
          sx={{
            position: 'absolute',
            color: scoreColor,
            '& circle': {
              strokeLinecap: 'round',
            },
          }}
        />
        
        {/* スコア数値 */}
        <Typography 
          variant="caption" 
          sx={{ 
            color: isLowRatingCount ? 'rgba(255, 255, 255, 0.6)' : 'white', 
            fontWeight: '600', 
            lineHeight: 1,
            fontSize: '0.7rem',
            letterSpacing: '0.02em',
            textShadow: '0 1px 2px rgba(0,0,0,0.8)',
            position: 'relative',
          }}
        >
          {normalizedScore.toFixed(2)}
        </Typography>
      </Box>
    );
  };

  // ステータス編集は**ライブラリ外でも出す**。アニメ・映画（AnimeCard / MovieCard）と同じ条件。
  // 検索結果で既にステータスを付けている作品のスコアやレビューを、あとから直す導線がここしかない。
  // （メニュー自体が公開モードでは開かないが、条件は3メディアで揃えておく）
  const canEditStatus = status !== null && mode !== 'public';
  // メモはゲーム固有の機能で、ライブラリ限定のまま。
  const canEditMemo = mode === 'profile' && status !== null;

  const handleEditStatus = () => {
    setAnchorEl(null);
    // 編集モード：initialData を渡さず開く → GameReviewDialog が自己 fetch する（ハイブリッド動作）
    setReviewDialogIsEdit(true);
    setOpenReviewDialog(true);
  };

  const handleEditMemo = async () => {
    setAnchorEl(null);
    if (hasMemo) {
      // 既存メモは内容を取得してからエディタを直接起動
      try {
        const res = await getGameMemo(game.game_id);
        setMemoEditorInitial(res.data?.memo ?? '');
      } catch {
        setMemoEditorInitial('');
      }
    } else {
      setMemoEditorInitial('');
    }
    setMemoEditorOpen(true);
  };

  const handleAddToFolder = () => {
    // フォルダピッカーはメニューと同じ位置に開くので、閉じる前にアンカーを退避する
    const a = anchorEl;
    handleClose();
    setFolderPickerAnchor(a);
  };

  const handleAddToList = () => {
    const a = anchorEl;
    handleClose();
    setListPickerAnchor(a);
  };

  const handleLaunchSteam = () => {
    logSteamLaunch(game.game_id);
    window.open(`steam://run/${game.steam_id}`, '_self');
  };

  const renderPlatformInfo = () => {
    if (!initialPlatform || mode !== 'profile' && mode !== 'public') return null;

    return (
      <Box
        sx={{
          position: 'absolute',
          maxWidth: { xs: '45%', sm: '70%' },
          bottom: 4,
          left: 4,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          borderRadius: '4px',
          padding: '0px 4px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Typography 
          variant="caption" 
          sx={{ 
            color: 'white',
            fontSize: { xs: '0.5rem', sm: '0.6rem' },
            fontWeight: 'medium',
            lineHeight: 1.2,
            opacity: 0.9
          }}
        >
          {getPlatformDisplayText(initialPlatform)}
        </Typography>
      </Box>
    );
  };

  // 未発売ゲームかどうかをチェック
  const isUpcomingGame = React.useMemo(() => {
    if (!game.first_release_date) return false;
    const releaseDate = new Date(game.first_release_date);
    const today = new Date();
    return releaseDate > today;
  }, [game.first_release_date]);

  const handleWishlistClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    try {
      // 既にWishlist追加済みならステータスを削除、そうでなければステータス5（気になる）に設定
      const newStatus = isWishlisted ? null : 5;
      await setGameStatus(game.game_id, newStatus);
      setStatus(newStatus);
      setIsWishlisted(!isWishlisted);
      setIsInitialStatus(false);
    } catch (error: any) {
      setErrorMessage(error.message || t('errorOccurred'));
      setOpenErrorDialog(true);
    }
  };

  const handlePriorityChange = async (newPriority: number) => {
    try {
      await setGamePriority(game.game_id, newPriority);
      setPriority(newPriority);
    } catch (error) {
      console.error('優先度の更新に失敗しました:', error);
      // エラー処理
    }
  };

  // プライオリティスライダーを表示するかどうかを判断するロジック
  const shouldShowPrioritySlider = React.useMemo(() => {
    return priorityMode && status !== 1 && status !== 2;
  }, [priorityMode, status]);

  const renderPriorityBadge = () => {
    // ステータス1または2の場合は表示しない
    if (priority < 1 || status === 1 || status === 2) return null;

    return (
      <Box
        sx={{
          backgroundColor: 'rgba(1, 62, 133, 0.37)',
          backdropFilter: 'blur(4px)',
          borderRadius: '4px',
          padding: '1px 4px',
          display: 'flex',
          alignItems: 'center',
          mb: score !== null ? 0.75 : 0,
          border: '0px solid rgba(32, 136, 255, 0.2)',
        }}
      >
        <Typography 
          variant="caption" 
          sx={{ 
            color: '#ffffff', 
            lineHeight: 1,
            fontSize: '0.6rem',
            letterSpacing: '0.02em',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: '0.6rem', mr: 0.2 }} />
          {priority}
        </Typography>
      </Box>
    );
  };


  return (
    <>
      <Box
        component={RouterLink}
        to={`/game/${game.game_id}`}
        sx={{
          textDecoration: 'none',
          display: 'block',
          height: '100%',
        }}
        onClick={(e) => {
          if (isStatusChanging) {
            e.preventDefault();
          }
        }}
        onContextMenu={handleContextMenu}
      >
        <Card
          elevation={0}
          sx={(theme) => ({
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            // 地と同色のベタ塗りにしない。カードを背景へ溶かしたいだけなら透明で足りる。
            // 実際に敷いていると、地が単色でなくなった瞬間（プロフィールの環境光など）に
            // カード下部だけ #121212 の四角として浮き上がる。
            backgroundColor: 'transparent',
            borderRadius: cardRadius,
            overflow: 'hidden',
            position: 'relative',
            ...cardHoverSx(theme),
          })}
        >
          <CardMedia
            component="div"
            sx={{
              height: 0,
              paddingTop:
                cardSize === 'square'
                  ? '100%'
                  : cardSize === 'landscape'
                  ? '46.74%' // Steam header 460x215 ratio
                  : '133.33%', // 標準 3:4
              borderRadius: cardRadius,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* 画像は独立した層に置く。CardMedia ごと拡大すると
                上に載っているバッジやステータスボタンまで一緒に拡大されてしまう。 */}
            <Box
              className={CARD_ZOOM_CLASS}
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `url("${
                  cardSize === 'landscape' && game.steam_id
                    ? `https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/${game.steam_id}/header.jpg`
                    : game.cover || '/image/no_image.png'
                }")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <Box className={CARD_SHEEN_CLASS} sx={CARD_SHEEN_SX} />
            <Box className={CARD_EDGE_CLASS} sx={CARD_EDGE_SX} />
            {uiVisible && renderPlatformInfo()}
            {uiVisible && (
            <Box
              sx={{
                position: 'absolute',
                top: 4,
                left: 4,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {renderAchievementIcon()}
              {renderReplayIcon()}
              {renderEndlessIcon()}
            </Box>
            )}
            {/* 右上アクション群：レビュー（上）→ メモ（下）の順で縦に並べる */}
            {uiVisible && (((mode === 'profile' || mode === 'public') && initialReviewText && initialReviewId) || (mode === 'profile' && hasMemo)) ? (
              <Box
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 0.5,
                }}
              >
                {/* レビュー（テキスト本文有り）インジケータ：own / public 両方で表示。詳細ページへ遷移 */}
                {(mode === 'profile' || mode === 'public') && initialReviewText && initialReviewId && (
                  <IconButton
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setReviewPreviewOpen(true);
                    }}
                    aria-label={t('aria.openReview')}
                    sx={{
                      padding: '2px 4px',
                      minWidth: 0,
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: 'rgba(0, 0, 0, 0.55)',
                      backdropFilter: 'blur(4px)',
                      color: 'rgba(255, 255, 255, 0.85)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.75)',
                        color: 'primary.main',
                      },
                    }}
                  >
                    <RateReviewOutlinedIcon sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }} />
                  </IconButton>
                )}

                {/* メモインジケータ：own profile かつ has_memo=true の時のみ。クリックでプレビュー */}
                {mode === 'profile' && hasMemo && (
                  <IconButton
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setMemoPreviewOpen(true);
                    }}
                    aria-label={t('aria.openMemo')}
                    sx={{
                      padding: '2px 4px',
                      minWidth: 0,
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: 'rgba(0, 0, 0, 0.55)',
                      backdropFilter: 'blur(4px)',
                      color: 'rgba(255, 255, 255, 0.85)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.75)',
                        color: 'primary.main',
                      },
                    }}
                  >
                    <StickyNote2OutlinedIcon sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }} />
                  </IconButton>
                )}
              </Box>
            ) : null}
            {uiVisible && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end'
              }}
            >
              {renderPriorityBadge()}
              {renderScore()}
            </Box>
            )}
            {uiVisible && renderJpScore()}
          </CardMedia>
          {(uiVisible || titleVisible) && (
          <CardContent sx={{ flexGrow: 1, p: 0, pt: 1, '&:last-child': { pb: 1 } }}>
            {titleVisible && (
            <Typography variant="subtitle2" component="div" noWrap sx={{
              fontSize: { xs: '0.65rem', sm: '0.7rem' },
              fontWeight: '600',
              mb: 0
            }}>
              {gameTitle}
            </Typography>
            )}
            {uiVisible && (
            <Box sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              minWidth: 0
            }}>
              {(mode === 'profile' || mode === 'public') ? (
                renderPlayInfo()
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
                  {getFormattedReleaseYear(game.first_release_date)}
                </Typography>
              )}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                ml: 0.5,
                flexShrink: 0,
              }}>
                {isUpcomingGame ? (
                  <Button
                    size="small"
                    onClick={handleWishlistClick}
                    sx={{ 
                      height: { xs: '20px', sm: '22px' },
                      minWidth: { xs: '60px', sm: '68px' },
                      padding: { xs: '2px 6px', sm: '3px 8px' },
                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                      lineHeight: 1.2,
                      textTransform: 'none',
                      color: isWishlisted ? 'primary.main' : 'pulse.info',
                      backgroundColor: isWishlisted ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.05)',
                      border: isWishlisted ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: isWishlisted ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.12)',
                        color: isWishlisted ? 'primary.main' : 'primary.main',
                      },
                    }}
                  >
                    {isWishlisted ? t('wishlistButton.added') : t('wishlistButton.add')}
                  </Button>
                ) : (
                  <Tooltip title={getStatusText()} arrow>
                    <IconButton
                      size="small"
                      onClick={handleClick}
                      aria-label={getStatusText()}
                      sx={{ p: '8px', m: '-8px' }}
                    >
                      {getStatusIcon(status)}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
            )}
            {uiVisible && shouldShowPrioritySlider && (
              <Box sx={{ mt: 1 }}>
                <PrioritySlider
                  value={priority}
                  onChange={handlePriorityChange}
                  disabled={mode === 'public'}
                />
              </Box>
            )}
          </CardContent>
          )}
          <GameCardStatusMenu
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            gameTitle={gameTitle}
            status={status}
            onSelectStatus={handleStatusChange}
            // マイページ以外（検索・一覧など未登録のゲーム）はステータス以外に項目がほぼ無いので、
            // 横に畳む意味がない。素直な縦リストにする。
            variant={mode === 'profile' ? 'segmented' : 'list'}
            onEditStatus={canEditStatus ? handleEditStatus : undefined}
            onEditMemo={canEditMemo ? handleEditMemo : undefined}
            hasMemo={hasMemo}
            // 縦リスト（プロフィール外）には出さない。あちらはステータスだけの素直なメニューに保つ。
            // 未ログインでも出さない（自分のリストしか出せないので、押してからログインへ送る意味が無い）
            onAddToList={mode === 'profile' && isLoggedIn ? handleAddToList : undefined}
            onAddToFolder={mode === 'profile' && folders ? handleAddToFolder : undefined}
            onLaunchSteam={game.steam_id && userSteamId && isMdUp ? handleLaunchSteam : undefined}
            onDelete={status !== null ? () => handleStatusChange(null) : undefined}
          />
        </Card>
      </Box>
      <ConfirmDialog
        open={openDeleteConfirmDialog}
        title={t('deleteConfirm.title')}
        message={t('deleteConfirm.message')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setOpenDeleteConfirmDialog(false)}
        confirmText={t('menu.delete')}
        cancelText={t('cancel', { ns: 'common' })}
      />
      <Dialog
        open={openErrorDialog}
        onClose={() => setOpenErrorDialog(false)}
      >
        <DialogTitle>{t('error', { ns: 'common' })}</DialogTitle>
        <DialogContent>
          <Typography>{errorMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setOpenErrorDialog(false)} color="primary">
            {t('close', { ns: 'common' })}
          </MuiButton>
        </DialogActions>
      </Dialog>
      {openReviewDialog && (
        <GameReviewDialog
          open={openReviewDialog}
          onClose={() => {
            setOpenReviewDialog(false);
            setReviewDialogIsEdit(false);
          }}
          gameId={game.game_id}
          // 編集モード: initialData を渡さず → ダイアログが自己 fetch（既存データを取得）
          // 新規モード: 空データ(EMPTY_REVIEW_DATA)を渡す → 空フォームで開く。
          //   オブジェクトリテラルを毎レンダリングで作ると参照が変わり、ダイアログの
          //   初期化 effect が再実行されて入力中の内容がリセットされるため、
          //   EMPTY_PLATFORMS と同様にモジュール定数で参照を固定する。
          initialData={reviewDialogIsEdit ? undefined : EMPTY_REVIEW_DATA}
          platforms={EMPTY_PLATFORMS}
          releaseDate={game.first_release_date || undefined}
          status={status}
          onReviewSaved={(reviewData: GameReviewData) => {
            setIsInitialStatus(false);
            setOpenReviewDialog(false);
            setReviewDialogIsEdit(false);
          }}
        />
      )}

      {/* レビュープレビュー：own profile / public 両方でトリガー */}
      {reviewPreviewOpen && initialReviewId && (
        <GameReviewPreviewDialog
          open={reviewPreviewOpen}
          onClose={() => setReviewPreviewOpen(false)}
          gameId={game.game_id}
          reviewId={initialReviewId}
          onRequestEdit={mode === 'profile' ? () => {
            setReviewPreviewOpen(false);
            setReviewDialogIsEdit(true);
            setOpenReviewDialog(true);
          } : undefined}
        />
      )}

      {/* メモプレビュー（閲覧）：own profile かつ has_memo=true 時にしかトリガーされない */}
      {memoPreviewOpen && (
        <Suspense fallback={null}>
          <MemoPreviewDialog
            open={memoPreviewOpen}
            gameId={game.game_id}
            gameName={gameTitle}
            onClose={() => setMemoPreviewOpen(false)}
            onRequestEdit={(initialValue) => {
              // プレビュー → 編集ダイアログへバトンタッチ
              setMemoPreviewOpen(false);
              setMemoEditorInitial(initialValue);
              setMemoEditorOpen(true);
            }}
          />
        </Suspense>
      )}

      {/* メモ編集ダイアログ */}
      {memoEditorOpen && (
        <Suspense fallback={null}>
          <MemoEditorDialog
            open={memoEditorOpen}
            value={memoEditorInitial}
            onSave={async (newValue) => {
              await saveGameMemo(game.game_id, newValue);
              setHasMemo(newValue.trim().length > 0);
              setMemoEditorInitial(newValue);
            }}
            onDelete={async () => {
              await deleteGameMemo(game.game_id);
              setHasMemo(false);
              setMemoEditorInitial('');
            }}
            onClose={() => setMemoEditorOpen(false)}
            gameId={game.game_id}
          />
        </Suspense>
      )}

      {/* フォルダ：チェックリスト式ピッカー */}
      {folders && (
        <FolderPickerPopover
          open={Boolean(folderPickerAnchor)}
          anchorEl={folderPickerAnchor}
          onClose={() => setFolderPickerAnchor(null)}
          folders={folders}
          gameId={game.game_id}
          onChange={() => onFolderChange?.()}
        />
      )}

      {/* リスト：追加専用ピッカー（所属状態は一覧APIから取れないため追加のみ）。
          閉じるアニメーションを出すため、アンカーの有無ではなくログイン状態でマウントを決める
          （Popover は open=false なら何も描画しないので常時マウントでも安い） */}
      {mode === 'profile' && isLoggedIn && (
        <GameListPickerPopover
          open={Boolean(listPickerAnchor)}
          anchorEl={listPickerAnchor}
          onClose={() => setListPickerAnchor(null)}
          gameId={game.game_id}
        />
      )}
    </>
  );
};

export default GameCard;
